import type { Application, ApplicationStatus, NudgeTier } from './api';
import { APPLICATION_STATUSES } from './api';

/**
 * Pure grouping/formatting logic for the Track tab's stage board — no fetch,
 * no React, no DOM. Mirrors jobBuckets.ts's shape exactly (group by an
 * already-server-assigned key, in the server's own canonical order; format
 * a served signal honestly; never re-derive what the backend already
 * decided). The six stages here are the SAME APPLICATION_STATUSES union the
 * backend's DB CHECK enforces (packages/schema/src/applications.ts) — this
 * module never invents a stage the schema doesn't have.
 */

/**
 * LANE U2 — the Track board's DISPLAY-ONLY stage label map (Jason, direct:
 * "[ Screening booked - Onsite should change to Interviewing}"). This is a
 * presentation-layer rename ONLY: the underlying `ApplicationStatus` key
 * (`screen`, `onsite`, ...) is byte-identical to api.ts's APPLICATION_STATUSES
 * / packages/schema's DB CHECK / applicationTransition.ts's planTransition —
 * none of which read a label, ever (grep confirms: both only switch on the
 * KEY). Superseding api.ts's own APPLICATION_STATUS_LABEL for every render
 * this board touches (column headers + the per-card stage <select>) so the
 * two labels can't drift apart on this board; api.ts's map is left untouched
 * for any other reader of that export.
 *
 * Jason's bracket was ambiguous about which word maps to which noun — the
 * applied reading (see this lane's report for the full table): Screen ->
 * "Screening booked", Onsite -> "Interviewing". Wishlist/Applied/Offer/Closed
 * were UNCHANGED at that time (not named in the request).
 *
 * LANE U7 (2026-07-05, docs/design/copy-review-2026-07-05.md §3.5 — Jason's
 * picked "Friend" voice, applied to the side-by-side comparison): extends the
 * two U2 renames with the other four, completing the Friend set for every
 * stage — Wishlist -> "On my radar", Offer -> "Offer's in!", Closed ->
 * "Closed out". Screen/Onsite are already the Friend wording from U2 (no
 * change). Display-only, same as U2 — the ApplicationStatus keys below are
 * byte-identical, untouched.
 *
 * KNOWN DRIFT (U5 finding, not resolved by this lane — flagged, not silently
 * compounded): THREE label sources exist for this same six-stage concept —
 * this map (api.ts's own APPLICATION_STATUS_LABEL, a second set), and
 * jobBuckets.ts's trackingStatusLabel() (a third set, feeding the Jobs tab's
 * tracking chip). This lane applies the Friend set ONLY to this Track-board
 * map per Jason's pick 11 — the other two sources are a separate
 * consolidation follow-up, not silently touched here.
 */
export const TRACKER_STAGE_DISPLAY_LABEL: Record<ApplicationStatus, string> = {
  wishlist: 'On my radar',
  applied: 'Applied',
  screen: 'Screening booked',
  onsite: 'Interviewing',
  offer: "Offer's in!",
  closed: 'Closed out',
};

/**
 * LANE U7 pick 12 (docs/design/copy-review-2026-07-05.md §3.5's "Stage-aware
 * empty states" table, Friend column) — replaces the single flat
 * "Nothing here yet." StageColumn.tsx rendered for every empty column
 * regardless of stage (the §2.3 finding: Wishlist-empty on day one and
 * Offer-empty after weeks of applying are not the same feeling, but were the
 * same six words). One honest variant per stage, keyed by the SAME real
 * ApplicationStatus — display-only, no new signal, no key change.
 */
export const STAGE_EMPTY_TEXT: Record<ApplicationStatus, string> = {
  wishlist: "Nothing saved here yet — that's normal on day one.",
  applied: "Nothing you've applied to yet.",
  screen: 'Nothing in screening right now.',
  onsite: 'No interviews lined up right now.',
  offer: 'No offers on the table yet — they take time.',
  closed: 'Nothing closed out.',
};

export interface StageColumn {
  status: ApplicationStatus;
  label: string;
  applications: Application[];
}

/** Group applications into the six board columns, in APPLICATION_STATUSES
 *  order (Wishlist -> Applied -> Screen -> Onsite -> Offer -> Closed) —
 *  matching apps/web/src/pages/Tracker.tsx's own column order exactly (the
 *  brief's "COPY shapes verbatim" law). Unlike jobBuckets.ts's
 *  groupJobsByBucket (which omits an empty group), EVERY column is always
 *  present here, even with zero cards — a kanban board with a permanently
 *  missing column reads as broken, not as "nothing to show," so this keeps
 *  Wishlist/Applied/etc. visible at count 0 rather than collapsing them. */
export function groupApplicationsByStage(applications: Application[]): StageColumn[] {
  const byStatus = new Map<ApplicationStatus, Application[]>();
  for (const status of APPLICATION_STATUSES) byStatus.set(status, []);
  for (const app of applications) {
    const arr = byStatus.get(app.status);
    if (arr) arr.push(app);
    // An application whose status somehow isn't in APPLICATION_STATUSES
    // (should be impossible under the DB CHECK) is silently NOT dropped from
    // the input array the caller holds — it simply has no column to render
    // in here, matching the "kept, never deleted, never hidden" law at the
    // data layer (this is a presentation-only omission, not a data loss).
  }
  return APPLICATION_STATUSES.map((status) => ({
    status,
    label: TRACKER_STAGE_DISPLAY_LABEL[status],
    applications: byStatus.get(status) ?? [],
  }));
}

/** TRACK-FIDELITY (2026-07-06) — the tracked-card click target. Returns the
 *  `/jobs/:jobId` route path when the server's OWN `jobAvailable` flag (GET
 *  /applications, applications.routes.ts — resolved via the SAME shared
 *  loadJobForUser loader GET /jobs/:id itself calls) says the id still
 *  resolves; `null` when it doesn't, so the caller renders an honest
 *  "no longer available" state instead of a dead link. This module never
 *  re-derives availability from any other field (title/company being
 *  present is NOT a proxy for availability — a leftJoin can carry stale
 *  jobTitle/jobCompany for a job the canonical resolve can no longer find,
 *  and vice versa) — it only relays the one boolean the server computed. */
export function applicationJobHref(
  app: Pick<Application, 'jobId' | 'jobAvailable'>,
): string | null {
  return app.jobAvailable ? `/jobs/${app.jobId}` : null;
}

/** Honest, human copy per nudge tier — the SILENCE READ the brief asks for
 *  ("14 days quiet — offer: mark closed?"). Wording states only what the
 *  server actually computed (silenceDays, a real count) and frames the
 *  close-question as an OFFER, never a statement of fact about what will
 *  happen — matches applicationSilence.ts's own doc comment ("never
 *  auto-sent, never auto-moved"). Returns null when there is nothing to
 *  say (tier is null — either not 'applied', or under the gentlest
 *  threshold), so a caller never has to invent copy for the quiet case. */
export function silenceLabel(app: Pick<Application, 'silenceDays' | 'nudgeTier'>): string | null {
  if (app.silenceDays == null || app.nudgeTier == null) return null;
  const days = app.silenceDays;
  const plural = days === 1 ? 'day' : 'days';
  return `${days} ${plural} quiet`;
}

/** The tier-specific tone word for a badge/border accent — presentation
 *  metadata only, never a claim the server didn't make. Null tier -> null
 *  (no badge at all, not a default "quiet" badge on a fresh application). */
export function tierTone(tier: NudgeTier | null): 'gentle' | 'firm' | 'stale' | null {
  return tier;
}

/** Whether the "mark closed?" offer should render — mirrors the server's own
 *  `closeOffered` boolean exactly (never re-derived from silenceDays/tier
 *  client-side, since the server's tier thresholds are policy DATA that can
 *  be retuned without a client deploy — this module must not hardcode 21). */
export function shouldOfferClose(app: Pick<Application, 'closeOffered'>): boolean {
  return app.closeOffered === true;
}

/** A short, honest count line for a column header — "3" not "3 applications"
 *  (the label above the column already says what it is). Kept as its own
 *  function (trivial today) so a future "3 of 12" or similar richer count
 *  has exactly one place to change. */
export function stageCount(column: Pick<StageColumn, 'applications'>): number {
  return column.applications.length;
}

/** Total applications across every column — the board's own "how many am I
 *  tracking" figure, computed from what was actually served (never a
 *  separate count endpoint's number, which could disagree). */
export function totalTracked(applications: Application[]): number {
  return applications.length;
}
